import React, { useCallback } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactDollarField, {
  dollarAmountDefaultValue,
  dollarAmountTwoDecimalDefaultValue,
  dollarToInt,
  numberIsGreaterThanZero,
} from 'shared/design-system/components/TrueImpactDollarField';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { KindDonation } from './utils';
import BudgetDonationsTooltips from './BudgetDonationsTooltips';

interface BudgetKindDonationRowProps {
  index: number;
  donation: KindDonation;
  setDonationsList: React.Dispatch<React.SetStateAction<KindDonation[]>>;
}

const defaultValue = dollarAmountDefaultValue;
const BudgetKindDonationRow: React.FC<BudgetKindDonationRowProps> = ({
  donation,
  index,
  setDonationsList,
}) => {
  const handleChange = useCallback(
    (type: 'hours' | 'perHour', value: string) => {
      setDonationsList(s => {
        const newState = [...s];
        newState[index][type] = value;
        if (
          value &&
          value !== defaultValue &&
          value !== dollarAmountTwoDecimalDefaultValue
        ) {
          newState[index].disabledTotal = true;
          newState[index].total = numberWithCommas(
            (
              dollarToInt(newState[index].hours) *
              dollarToInt(newState[index].perHour)
            ).toFixed(2),
          );
        } else {
          newState[index].disabledTotal = false;
        }
        return newState;
      });
    },
    [setDonationsList],
  );
  const handleChangeTotal = useCallback(
    (value: string) => {
      setDonationsList(s => {
        const newState = [...s];
        newState[index].total = value;
        if (
          value &&
          value !== defaultValue &&
          value !== dollarAmountTwoDecimalDefaultValue
        ) {
          newState[index].hours = defaultValue;
          newState[index].perHour = defaultValue;
          newState[index].disabledHourly = true;
        } else {
          newState[index].disabledHourly = false;
        }
        return newState;
      });
    },
    [setDonationsList],
  );
  return (
    <>
      <TrueImpactText variant="body-bold" className="left-column-text">
        {donation.text}
      </TrueImpactText>
      <TrueImpactDollarField
        value={donation.perHour}
        placeholder="Hours or units"
        disabled={donation.disabledHourly}
        error={
          donation.errorInPerHoursField &&
          !numberIsGreaterThanZero(donation.perHour)
        }
        hideIcon={true}
        onBlur={() =>
          donation.perHour &&
          handleChange(
            'perHour',
            numberWithCommas(dollarToInt(donation.perHour).toFixed(2)),
          )
        }
        onChange={v => handleChange('perHour', v)}
      />
      <TrueImpactDollarField
        value={donation.hours}
        placeholder="Value per hour/unit"
        disabled={donation.disabledHourly}
        error={
          donation.errorInHoursField && !numberIsGreaterThanZero(donation.hours)
        }
        onBlur={() =>
          donation.hours &&
          handleChange(
            'hours',
            numberWithCommas(dollarToInt(donation.hours).toFixed(2)),
          )
        }
        onChange={v => handleChange('hours', v)}
      />
      <TrueImpactDollarField
        value={donation.total}
        placeholder="Total value"
        disabled={donation.disabledTotal}
        onBlur={() =>
          donation.total &&
          handleChangeTotal(
            numberWithCommas(dollarToInt(donation.total).toFixed(2)),
          )
        }
        onChange={handleChangeTotal}
      />
      <BudgetDonationsTooltips
        text={donation.text}
        className="calculator-info-icon"
      />
    </>
  );
};

export default BudgetKindDonationRow;
